import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import * as React from 'react';
import { UsernameAuthForm } from '../../../../components/UsernameAuthForm';
import { ContentLayout } from '../../../../components/ContentLayout';
import { EActiveFlow } from '../../../../types/flow';
import { isEmail, isValidPhone, isPhone } from '../../../../utils/validation';
import { formatPhone } from '../../../../utils/formatPhone';
import { EActiveView } from '../../../../types/view';
import { trackChangeLoginFlowTo, trackForgotPasswordClick, trackLoginResumeButtonClick, trackShowTwoStepPassword } from '../../../../utils/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
export function UsernameAuthFormContainer(_ref) {
  var isSubmitting = _ref.isSubmitting,
    activeFlow = _ref.activeFlow,
    submitButtonText = _ref.submitButtonText,
    login = _ref.login,
    loginError = _ref.loginError,
    isLoginInvalid = _ref.isLoginInvalid,
    hasAsyncLoginError = _ref.hasAsyncLoginError,
    password = _ref.password,
    passwordError = _ref.passwordError,
    isPasswordInvalid = _ref.isPasswordInvalid,
    hasAsyncPasswordError = _ref.hasAsyncPasswordError,
    hasAsyncPhoneError = _ref.hasAsyncPhoneError,
    phoneError = _ref.phoneError,
    changeLogin = _ref.changeLogin,
    changePassword = _ref.changePassword,
    authenticate = _ref.authenticate,
    register = _ref.register,
    checkEmail = _ref.checkEmail,
    changeView = _ref.changeView,
    changeFlow = _ref.changeFlow,
    resetToPhoneAuth = _ref.resetToPhoneAuth,
    changeToPhoneAuth = _ref.changeToPhoneAuth,
    autoFocusDisabled = _ref.autoFocusDisabled,
    isProfessionalUser = _ref.isProfessionalUser,
    changeIsProfessionalUser = _ref.changeIsProfessionalUser,
    source = _ref.source;
  // FORM
  var _React$useState = React.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    changed = _React$useState2[0],
    setChanged = _React$useState2[1];
  var _React$useState3 = React.useState(false),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    touched = _React$useState4[0],
    setTouched = _React$useState4[1];

  // LOGIN
  var loginRef = React.useRef(null);
  var handleLoginChange = function handleLoginChange(e) {
    var value = e.currentTarget.value;
    changeLogin(value.trim());
    setChanged(true);
  };
  React.useEffect(function () {
    if (loginRef.current) {
      loginRef.current.focus();
    }
  }, []);

  // PASSWORD
  var passwordRef = React.useRef(null);
  var handlePasswordChange = function handlePasswordChange(e) {
    var value = e.currentTarget.value;
    changePassword(value);
    setChanged(true);
  };
  var _React$useState5 = React.useState(false),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    passwordShown = _React$useState6[0],
    setPasswordShown = _React$useState6[1];
  var togglePasswordVisibility = React.useCallback(function () {
    return setPasswordShown(!passwordShown);
  }, [passwordShown]);
  var handleResetLinkClick = React.useCallback(function (e) {
    e.preventDefault();
    trackForgotPasswordClick(source);
    changeView(EActiveView.Restore, isEmail(login) ? {
      email: login
    } : undefined);
  }, [changeView, login, source]);

  // PHONE
  var phoneRef = React.useRef(null);
  var _React$useState7 = React.useState(''),
    _React$useState8 = _slicedToArray(_React$useState7, 2),
    phone = _React$useState8[0],
    setPhone = _React$useState8[1];
  var isPhoneInvalid = React.useMemo(function () {
    return phone !== '' && !isValidPhone(phone);
  }, [phone]);
  var handlePhoneChange = function handlePhoneChange(value) {
    setPhone(value);
    setChanged(true);
  };

  // PRO USER
  var handleProfessionalUserChange = React.useCallback(function (e, value) {
    changeIsProfessionalUser(value);
  }, [changeIsProfessionalUser]);

  // SUBMITION
  var handleFocusInvalid = React.useCallback(function () {
    if (isLoginInvalid && loginRef.current) {
      loginRef.current.focus();
      return;
    }
    if (isPasswordInvalid && passwordRef.current) {
      passwordRef.current.focus();
      return;
    }
    if (isPhoneInvalid && phoneRef.current) {
      phoneRef.current.focus();
      return;
    }
  }, [isPasswordInvalid, isPhoneInvalid, isLoginInvalid]);
  var handleSubmit = function handleSubmit() {
    setTouched(true);
    switch (activeFlow) {
      case EActiveFlow.Registration:
        if (isLoginInvalid || isPasswordInvalid || isPhoneInvalid) {
          handleFocusInvalid();
          return;
        }
        register({
          email: login,
          password: password,
          phone: phone,
          isProfessional: isProfessionalUser,
          source: source
        });
        break;
      case EActiveFlow.Authentication:
        if (isLoginInvalid || isPasswordInvalid) {
          handleFocusInvalid();
          return;
        }
        authenticate({});
        break;
      default:
        if (isLoginInvalid) {
          handleFocusInvalid();
          return;
        }
        trackLoginResumeButtonClick(source);
        if (isEmail(login)) {
          checkEmail();
        } else {
          trackShowTwoStepPassword('id', source);
          changeFlow(EActiveFlow.Authentication);
        }
    }
    setChanged(false);
  };
  var switchView = function switchView() {
    trackChangeLoginFlowTo('phone', source);
    if (isPhone(login)) {
      var _phone = formatPhone(login);
      changeToPhoneAuth(_phone);
    } else {
      resetToPhoneAuth();
    }
  };
  React.useEffect(function () {
    setPasswordShown(false);
    setPhone('');
    if ((activeFlow === EActiveFlow.Registration || activeFlow === EActiveFlow.Authentication) && passwordRef.current) {
      setTouched(false);
      setChanged(false);
      passwordRef.current.focus();
    }
  }, [activeFlow]);
  return /*#__PURE__*/_jsx(ContentLayout, {
    children: /*#__PURE__*/_jsx(UsernameAuthForm, {
      activeFlow: activeFlow,
      handleSubmit: handleSubmit,
      isSubmitting: isSubmitting,
      submitButtonText: submitButtonText,
      autoFocusDisabled: autoFocusDisabled
      // LOGIN
      ,
      loginRef: loginRef,
      login: login,
      onLoginChange: handleLoginChange,
      loginError: !changed && loginError || undefined,
      isLoginInvalid: touched && (isLoginInvalid || !changed && hasAsyncLoginError)
      // PASSWORD
      ,
      passwordRef: passwordRef,
      password: password,
      passwordShown: passwordShown,
      isPasswordInvalid: touched && (isPasswordInvalid || !changed && hasAsyncPasswordError),
      passwordError: passwordError,
      togglePasswordVisibility: togglePasswordVisibility,
      onPasswordChange: handlePasswordChange,
      onResetLinkClick: handleResetLinkClick
      // PHONE
      ,
      phoneRef: phoneRef,
      phone: phone,
      onPhoneChange: handlePhoneChange,
      isPhoneInvalid: isPhoneInvalid || !changed && hasAsyncPhoneError,
      phoneError: phoneError
      // PRO USER
      ,
      isProfessionalUser: isProfessionalUser,
      onProfessionalUserChange: handleProfessionalUserChange,
      onSwitchAuthByPhone: switchView
    })
  });
}